import React, { useState } from "react";

import { invalidateQuery } from "@/api";
import { toggleConsentManagementConfig } from "@/consent-management/api/toggleConsentManagementConfig";
import { consentManagementIntegrations } from "@/consent-management/consentManagement";
import { Banner } from "@/design-system/components/Banner/Banner";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import { unpackRequestErrorMessage } from "@/utils/request";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { ConsentLoadError } from "../ConsentLoadError";
import { ESSENTIAL_ALWAYS_SENT, versionBehavior } from "../copy";
import { findUncategorized } from "../draftStatus";
import { ConsentManagerStepProps } from "../types";
import {
  LOAD_ERROR_REASON,
  NO_WRITE_ACCESS_REASON,
  useFreshpaintConsent,
} from "../useFreshpaintConsent";

const otherVendorLiveMessage = (
  slug: keyof typeof consentManagementIntegrations,
) =>
  `${consentManagementIntegrations[slug].name} is already on in this environment. Turn it off before turning on the Freshpaint Consent Manager.`;

export function TurnOnStep({ goToStep, complete }: ConsentManagerStepProps) {
  const {
    config,
    destinations,
    canWrite,
    isLoading,
    loadError,
    retry,
    otherLiveConfig,
    fetchOtherLiveConfig,
  } = useFreshpaintConsent();
  const [turningOn, setTurningOn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const published = config?.activePublishedVersion;
  const uncategorized = findUncategorized(
    destinations,
    published?.consentMapping,
  );

  async function turnOn() {
    if (!config) {
      return;
    }
    setTurningOn(true);
    setError(null);
    try {
      const other = await fetchOtherLiveConfig();
      if (other) {
        setError(otherVendorLiveMessage(other.type));
        setTurningOn(false);
        return;
      }
      await toggleConsentManagementConfig({ id: config.id, enabled: true });
      await invalidateQuery(["get_consent_management_configs", {}]);
      complete();
    } catch (toggleError) {
      setError(unpackRequestErrorMessage(toggleError));
      setTurningOn(false);
    }
  }

  if (config?.enabled && published) {
    return (
      <WizardStepLayout
        title="The Consent Manager is on"
        description="Visitors see your latest published banner."
        primaryAction={{ label: "Finish", onClick: complete }}
      />
    );
  }

  return (
    <WizardStepLayout
      title="Turn on the Consent Manager"
      description={
        published
          ? `Once it’s on, visitors see the banner on their next page view. ${versionBehavior(published)} ${ESSENTIAL_ALWAYS_SENT}`
          : "Once it’s on, visitors see the banner on their next page view."
      }
      primaryAction={{
        label: "Turn on",
        onClick: turnOn,
        loading: turningOn,
        disabled:
          isLoading ||
          loadError ||
          !published ||
          uncategorized.length > 0 ||
          !!otherLiveConfig ||
          canWrite === false,
        disabledReason: loadError
          ? LOAD_ERROR_REASON
          : isLoading
            ? undefined
            : canWrite === false
              ? NO_WRITE_ACCESS_REASON
              : !published
                ? "Publish your banner first"
                : otherLiveConfig
                  ? `Turn off ${consentManagementIntegrations[otherLiveConfig.type].name} first`
                  : "Categorize every destination first",
      }}
    >
      {loadError ? (
        <ConsentLoadError retry={retry} />
      ) : isLoading ? (
        <LoadingSpinner className="size-6" />
      ) : otherLiveConfig ? (
        <Banner type="warning">
          {otherVendorLiveMessage(otherLiveConfig.type)}
        </Banner>
      ) : uncategorized.length > 0 ? (
        <Banner type="warning">
          The published version doesn’t categorize{" "}
          {uncategorized
            .map(({ saasDestination }) => saasDestination.name)
            .join(", ")}
          .{" "}
          <button
            type="button"
            className="cursor-pointer underline"
            onClick={() => goToStep("categorize")}
          >
            Categorize them
          </button>{" "}
          and publish again.
        </Banner>
      ) : (
        <Typography variant="body-sm" color="subtle">
          You can turn it off anytime from the Consent Management overview.
        </Typography>
      )}
      {error && <Banner type="error">{error}</Banner>}
    </WizardStepLayout>
  );
}
